/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from "Util/AttributeUtils";
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import { VisitorsProjectEntity } from 'Models/Security/Acl/VisitorsProjectEntity';
import { AptusAdminProjectEntity } from 'Models/Security/Acl/AptusAdminProjectEntity';
import { UserProjectEntity } from 'Models/Security/Acl/UserProjectEntity';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import {SuperAdministratorScheme} from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] on begin
import {store} from "../Store";
import gql from 'graphql-tag';
// % protected region % [Add any further imports here] end

export interface IProjectEntityAttributes extends IModelAttributes {
	name: string;
	clientJobNumber: string;
	aptusJobNumber: string;
	postcode: string;
	location: string;
	archived: boolean;
	country: Enums.countries;

	addressId?: string;
	address?: Models.AddressEntity | Models.IAddressEntityAttributes;
	orderss: Array<Models.OrderEntity | Models.IOrderEntityAttributes>;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	projectContactId?: string;
	projectContact?: Models.UserEntity | Models.IUserEntityAttributes;
	productss: Array<Models.ProductEntity | Models.IProductEntityAttributes>;
	priceVersionId?: string;
	priceVersion?: Models.PriceVersionEntity | Models.IPriceVersionEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] on begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('ProjectEntity', 'Project')
// % protected region % [Customise your entity metadata here] end
export default class ProjectEntity extends Model implements IProjectEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsProjectEntity(),
		new AptusAdminProjectEntity(),
		new UserProjectEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	/**
	 * Project Name
	 */
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	// % protected region % [Modify props to the crud options here for attribute 'Client Job Number'] off begin
	/**
	 * Job number provided by clients
	 */
	@observable
	@attribute()
	@CRUD({
		name: 'Client Job Number',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public clientJobNumber: string;
	// % protected region % [Modify props to the crud options here for attribute 'Client Job Number'] end

	// % protected region % [Modify props to the crud options here for attribute 'Aptus Job Number'] off begin
	/**
	 * Job number assigned by Aptus
	 */
	@Validators.Unique()
	@observable
	@attribute()
	@CRUD({
		name: 'Aptus Job Number',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public aptusJobNumber: string;
	// % protected region % [Modify props to the crud options here for attribute 'Aptus Job Number'] end

	// % protected region % [Modify props to the crud options here for attribute 'Postcode'] off begin
	@observable
	@attribute()
	@CRUD({
		name: 'Postcode',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public postcode: string;
	// % protected region % [Modify props to the crud options here for attribute 'Postcode'] end

	// % protected region % [Modify props to the crud options here for attribute 'Location'] off begin
	@observable
	@attribute()
	@CRUD({
		name: 'Location',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public location: string;
	// % protected region % [Modify props to the crud options here for attribute 'Location'] end

	// % protected region % [Modify props to the crud options here for attribute 'Archived'] off begin
	@observable
	@attribute()
	@CRUD({
		name: 'Archived',
		displayType: 'checkbox',
		order: 60,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public archived: boolean;
	// % protected region % [Modify props to the crud options here for attribute 'Archived'] end

	// % protected region % [Modify props to the crud options here for attribute 'Country'] off begin
	@observable
	@attribute()
	@CRUD({
		name: 'Country',
		displayType: 'enum-combobox',
		order: 70,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.countriesOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.countriesOptions),
		displayFunction: (attribute: Enums.countries) => Enums.countriesOptions[attribute],
	})
	public country: Enums.countries;
	// % protected region % [Modify props to the crud options here for attribute 'Country'] end

	/**
	 * Project has a default delivery address set
	 */
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Address'] off begin
		name: 'Address',
		displayType: 'reference-combobox',
		order: 80,
		referenceTypeFunc: () => Models.AddressEntity,
		// % protected region % [Modify props to the crud options here for reference 'Address'] end
	})
	public addressId?: string;
	@observable
	@attribute({isReference: true})
	public address: Models.AddressEntity;

	/**
	 * Project has many orders
	 */
	@observable
	@attribute({isReference: true})
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Orders'] off begin
		name: "Orderss",
		displayType: 'reference-multicombobox',
		order: 90,
		referenceTypeFunc: () => Models.OrderEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'orderss',
			oppositeEntity: () => Models.OrderEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Orders'] end
	})
	public orderss: Models.OrderEntity[] = [];

	/**
	 * Organisation has many projects
	 */
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Organisation'] off begin
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.OrganisationEntity,
		// % protected region % [Modify props to the crud options here for reference 'Organisation'] end
	})
	public organisationId?: string;
	@observable
	@attribute({isReference: true})
	public organisation: Models.OrganisationEntity;

	/**
	 * Project must be created by clients (Organisation users)
	 */
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Project Contact'] off begin
		name: 'Project Contact',
		displayType: 'reference-combobox',
		order: 110,
		referenceTypeFunc: () => Models.UserEntity,
		// % protected region % [Modify props to the crud options here for reference 'Project Contact'] end
	})
	public projectContactId?: string;
	@observable
	@attribute({isReference: true})
	public projectContact: Models.UserEntity;

	@observable
	@attribute({isReference: true})
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Products'] off begin
		name: "Productss",
		displayType: 'reference-multicombobox',
		order: 120,
		referenceTypeFunc: () => Models.ProductEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'productss',
			oppositeEntity: () => Models.ProductEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Products'] end
	})
	public productss: Models.ProductEntity[] = [];

	/**
	 * A project must have a price version
	 */
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Price Version'] off begin
		name: 'Price Version',
		displayType: 'reference-combobox',
		order: 130,
		referenceTypeFunc: () => Models.PriceVersionEntity,
		// % protected region % [Modify props to the crud options here for reference 'Price Version'] end
	})
	public priceVersionId?: string;
	@observable
	@attribute({isReference: true})
	public priceVersion: Models.PriceVersionEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IProjectEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IProjectEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.clientJobNumber !== undefined) {
				this.clientJobNumber = attributes.clientJobNumber;
			}
			if (attributes.aptusJobNumber !== undefined) {
				this.aptusJobNumber = attributes.aptusJobNumber;
			}
			if (attributes.postcode !== undefined) {
				this.postcode = attributes.postcode;
			}
			if (attributes.location !== undefined) {
				this.location = attributes.location;
			}
			if (attributes.archived !== undefined) {
				this.archived = attributes.archived;
			}
			if (attributes.country !== undefined) {
				this.country = attributes.country;
			}
			if (attributes.addressId !== undefined) {
				this.addressId = attributes.addressId;
			}
			if (attributes.address !== undefined) {
				if (attributes.address === null) {
					this.address = attributes.address;
				} else {
					if (attributes.address instanceof Models.AddressEntity) {
						this.address = attributes.address;
						this.addressId = attributes.address.id;
					} else {
						this.address = new Models.AddressEntity(attributes.address);
						this.addressId = this.address.id;
					}
				}
			}
			if (attributes.orderss !== undefined && Array.isArray(attributes.orderss)) {
				for (const model of attributes.orderss) {
					if (model instanceof Models.OrderEntity) {
						this.orderss.push(model);
					} else {
						this.orderss.push(new Models.OrderEntity(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else {
					if (attributes.organisation instanceof Models.OrganisationEntity) {
						this.organisation = attributes.organisation;
						this.organisationId = attributes.organisation.id;
					} else {
						this.organisation = new Models.OrganisationEntity(attributes.organisation);
						this.organisationId = this.organisation.id;
					}
				}
			}
			if (attributes.projectContactId !== undefined) {
				this.projectContactId = attributes.projectContactId;
			}
			if (attributes.projectContact !== undefined) {
				if (attributes.projectContact === null) {
					this.projectContact = attributes.projectContact;
				} else {
					if (attributes.projectContact instanceof Models.UserEntity) {
						this.projectContact = attributes.projectContact;
						this.projectContactId = attributes.projectContact.id;
					} else {
						this.projectContact = new Models.UserEntity(attributes.projectContact);
						this.projectContactId = this.projectContact.id;
					}
				}
			}
			if (attributes.productss !== undefined && Array.isArray(attributes.productss)) {
				for (const model of attributes.productss) {
					if (model instanceof Models.ProductEntity) {
						this.productss.push(model);
					} else {
						this.productss.push(new Models.ProductEntity(model));
					}
				}
			}
			if (attributes.priceVersionId !== undefined) {
				this.priceVersionId = attributes.priceVersionId;
			}
			if (attributes.priceVersion !== undefined) {
				if (attributes.priceVersion === null) {
					this.priceVersion = attributes.priceVersion;
				} else {
					if (attributes.priceVersion instanceof Models.PriceVersionEntity) {
						this.priceVersion = attributes.priceVersion;
						this.priceVersionId = attributes.priceVersion.id;
					} else {
						this.priceVersion = new Models.PriceVersionEntity(attributes.priceVersion);
						this.priceVersionId = this.priceVersion.id;
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		address {
			${Models.AddressEntity.getAttributes().join('\n')}
		}
		orderss {
			${Models.OrderEntity.getAttributes().join('\n')}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
		}
		projectContact {
			${Models.UserEntity.getAttributes().join('\n')}
		}
		productss {
			${Models.ProductEntity.getAttributes().join('\n')}
		}
		priceVersion {
			${Models.PriceVersionEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath = {
			orderss: {},
			productss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'orderss',
							'productss',
						]
					},
				],
			}
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}


	// % protected region % [Add any further custom model features here] on begin
	public static async fetchActiveProjects() {
		const dashboardProjectQuery = 
			gql` query dashboardProjects($args: [WhereExpressionGraph]) {
					projectEntitys(where: $args) {
						id
						created
						modified
						name
						clientJobNumber
						postcode
						location
						country
						archived
						organisationId
						projectContact {
						  id
						  firstName
						  lastName
						}
						orderss( orderBy: {path: "orderDate", descending: true} )
						{
						  id
						  orderDate
						}
					}
				}`
		
		const queryArgs = store.userGroups.some(ug => ug.hasBackendAccess) ? null :
			[{path: "organisationId", comparison: "equal", value: store.organisationId}]; 
		
		const results = await store.apolloClient
			.query({
				query: dashboardProjectQuery,
				variables: {
					"args": queryArgs
				},
				fetchPolicy: 'network-only',
			});
		
		return results.data['projectEntitys'].map((r: any) => new this(r));
	}
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [ createdAttr, modifiedAttr ] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ProjectEntity.prototype, 'created');
CRUD(modifiedAttr)(ProjectEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
